import { ROLES_DETAIL } from "@/constants/roles";
import { useAccessControlUserService } from "@/hooks/useAccessControlUser";
import type { Role, SegcenFormat } from "@/interfaces/AccessControl";
import { getFormatPermissions } from "@/mappings/accessControlUser";
import { validateSegcenPermissions } from "@/utils/accessControlUser";
import { getEnvironmentVariable } from "@/utils/environment";
import { getCurrentIdRoleFromSessionStorage, setCurrentIdRoleToSessionStorage } from "@/utils/storage";
import { UserAuthUtil } from "@/utils/userAuth";
import {
    createContext,
    useContext,
    useState,
    useMemo,
    useCallback,
    type ReactNode,
    type FC
} from "react";

interface UserAccessContextState {
    segcen: SegcenFormat;
    currentRole: Role | null;
    roleList: Role[];
    userName: string;
    fullName: string;
    isLoading?: boolean;
    updateRole: (rol: Role) => void;
    initUserAccessControlData: () => void;
}

interface UserAccessState {
    segcen: SegcenFormat;
    currentRole: Role | null;
    roleList: Role[];
    userName: string;
    fullName: string;
    isLoading?: boolean;
}

const UserAccessControlContext = createContext<UserAccessContextState | undefined>(undefined);

interface UserAccessControlProviderProps {
    children: ReactNode;
}

/**
 * 
 * @description el atributo isLoading es para saber, que ya termino de cargar este context,
 * por eso va al final se tiene que setear false
 */
export const UserAccessControlProvider: FC<UserAccessControlProviderProps> = ({ children }) => {
    const [userAccessControl, setUserAccessControl] = useState<UserAccessState>({
        segcen: {},
        currentRole: null,
        roleList: [],
        isLoading: true,
        userName: "",
        fullName: ""
    });
    const enabledRoles = getEnvironmentVariable("VITE_APP_ENABLED_ROLES", "").split(",");

    const { refetch } = useAccessControlUserService({ enabled: false });
    const initUserAccessControlData = useCallback(async () => {
        refetch().then(response => {

            if (response.data) {
                const permissions = getFormatPermissions(response.data);

                const roleList = getRoleList(permissions);

                if (!roleList.length) return;

                let currentRoleUser = null;

                const idRole = getCurrentIdRoleFromSessionStorage();

                if (roleList.length === 1) {
                    currentRoleUser = roleList[0];
                } else if (idRole) {
                    const roleFound = roleList.find(rol => rol.id === idRole) ?? null;
                    currentRoleUser = roleFound;
                }

                if (!enabledRoles.includes(String(currentRoleUser?.id))) currentRoleUser = null;

                setUserAccessControl((prevState) => {
                    return {
                        ...prevState,
                        segcen: permissions,
                        roleList,
                        currentRole: currentRoleUser,
                        userName: UserAuthUtil.getUserName(),
                        fullName: UserAuthUtil.getFullName(),
                        isLoading: false
                    }
                });
            }
        });
    }, [refetch]);

    const updateRole = (rol: Role) => {
        setCurrentIdRoleToSessionStorage(rol.id);
        setUserAccessControl((prevState) => {
            return {
                ...prevState,
                currentRole: rol,
            };
        });
    };

    const value = useMemo(
        () => ({
            ...userAccessControl,
            initUserAccessControlData,
            updateRole
        }),
        [initUserAccessControlData, userAccessControl]
    );

    return <UserAccessControlContext.Provider value={value}>{children}</UserAccessControlContext.Provider>;
};

export const useUserAccessControl = (): UserAccessContextState => {
    const context = useContext(UserAccessControlContext);
    if (!context) {
        throw new Error("useUserAccessControl debe ser usado con UserAccessControlProvider");
    }
    return context;
};

const getRoleList = (permissions: SegcenFormat) => {
    return ROLES_DETAIL.filter((rol) =>
        validateSegcenPermissions({
            segcen: permissions,
            object: rol.segcenObject,
            action: rol.segcenAction,
        })
    );
};